import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ArrowLeft, Save, Plus, Trash2, Move, Square, Circle, 
  DoorOpen, Grid3X3, Image, Layers, Settings, Eye, Edit3,
  ZoomIn, ZoomOut, RotateCcw, Home, Building2, MapPin, Upload, Loader2
} from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var elementTypes = [
  { id: "room", label: "Room", icon: Square },
  { id: "unit_marker", label: "Unit Marker", icon: Home },
  { id: "door", label: "Door", icon: DoorOpen },
  { id: "stairs", label: "Stairs", icon: Layers },
  { id: "label", label: "Label", icon: Edit3 }
];

var defaultColors = {
  available: "#22c55e",
  rented: "#3b82f6", 
  maintenance: "#f59e0b",
  reserved: "#8b5cf6"
};

export default function FloorPlan() {
  var { propertyId } = useParams();
  var [searchParams] = useSearchParams();
  var navigate = useNavigate();
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var canvasRef = useRef(null);

  var [property, setProperty] = useState(null);
  var [units, setUnits] = useState([]);
  var [floorPlan, setFloorPlan] = useState(null);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);

  var [viewMode, setViewMode] = useState("view");
  var [viewType, setViewType] = useState("grid");
  var [selectedFloor, setSelectedFloor] = useState(0);
  var [selectedElement, setSelectedElement] = useState(null);
  var [selectedTool, setSelectedTool] = useState("select");
  var [showGrid, setShowGrid] = useState(true);
  var [zoom, setZoom] = useState(1);
  var [unitDetailDialog, setUnitDetailDialog] = useState(null);
  var [settingsDialog, setSettingsDialog] = useState(false);
  var [uploadingImage, setUploadingImage] = useState(false);
  var [storageConfigured, setStorageConfigured] = useState(null);

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";

  useEffect(function() {
    if (propertyId) {
      fetchData();
      checkStorageConfig();
    }
  }, [propertyId]);

  async function checkStorageConfig() {
    try {
      var response = await fetch(API_URL + "/api/storage/config", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });
      if (response.ok) {
        var data = await response.json();
        setStorageConfigured(data.configured);
      }
    } catch (err) {
      setStorageConfigured(false);
    }
  }

  async function handleFloorPlanImageUpload(e) {
    var file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      var formData = new FormData();
      formData.append("file", file);
      formData.append("entity_type", "floor_plan");
      formData.append("entity_id", propertyId);
      formData.append("folder", "floor_plans/" + propertyId);
      
      var response = await fetch(API_URL + "/api/storage/upload", {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
        body: formData
      });
      
      if (response.ok) {
        var result = await response.json();
        // Update the current floor's background image
        if (floorPlan?.floors?.[selectedFloor]) {
          var floors = [...floorPlan.floors];
          floors[selectedFloor] = { ...floors[selectedFloor], background_image: result.file_url };
          setFloorPlan({ ...floorPlan, floors: floors });
        }
        toast.success(t("floorPlan.imageUploaded"));
      } else {
        var error = await response.json();
        toast.error(error.detail || t("fileUpload.uploadFailed"));
      }
    } catch (err) {
      toast.error(t("fileUpload.uploadFailed"));
    } finally {
      setUploadingImage(false);
    }
  }

  async function fetchData() {
    setLoading(true);
    try {
      var headers = token ? { Authorization: "Bearer " + token } : {};
      
      var [propRes, unitsRes, fpRes] = await Promise.all([
        fetch(API_URL + "/api/properties/" + propertyId, { credentials: "include", headers: headers }),
        fetch(API_URL + "/api/units?property_id=" + propertyId, { credentials: "include", headers: headers }),
        fetch(API_URL + "/api/properties/" + propertyId + "/floor-plan", { credentials: "include", headers: headers })
      ]);

      if (propRes.ok) {
        var propData = await propRes.json();
        setProperty(propData);
      }
      if (unitsRes.ok) {
        var unitsData = await unitsRes.json();
        setUnits(unitsData);
      }
      if (fpRes.ok) {
        var fpData = await fpRes.json();
        setFloorPlan(fpData);
        if (fpData.view_type) setViewType(fpData.view_type);
      }
    } catch (err) {
      console.error(err);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }

  async function saveFloorPlan() {
    setSaving(true);
    try {
      var headers = { 
        "Content-Type": "application/json",
        ...(token ? { Authorization: "Bearer " + token } : {})
      };
      
      var response = await fetch(API_URL + "/api/properties/" + propertyId + "/floor-plan", {
        method: "PUT",
        credentials: "include",
        headers: headers,
        body: JSON.stringify({
          view_type: viewType,
          floors: floorPlan?.floors || [],
          settings: floorPlan?.settings || { grid_size: 50, show_grid: true, unit_colors: defaultColors }
        })
      });

      if (response.ok) {
        toast.success(t("floorPlan.saved"));
        var updated = await response.json();
        setFloorPlan(updated);
      } else {
        toast.error(t("common.error"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  }

  function addFloor() {
    var floors = floorPlan?.floors || [];
    var newFloor = {
      floor_number: floors.length,
      floor_name: floors.length === 0 ? t("floorPlan.groundFloor") : t("floorPlan.floor") + " " + floors.length,
      elements: [],
      width: 800,
      height: 600
    };
    setFloorPlan({ ...floorPlan, floors: [...floors, newFloor] });
    setSelectedFloor(floors.length);
  }

  function deleteFloor(index) {
    var floors = [...(floorPlan?.floors || [])];
    floors.splice(index, 1);
    floors = floors.map(function(f, i) { return { ...f, floor_number: i }; });
    setFloorPlan({ ...floorPlan, floors: floors });
    if (selectedFloor >= floors.length) setSelectedFloor(Math.max(0, floors.length - 1));
  }

  function addElement(type, x, y) {
    if (!floorPlan?.floors?.[selectedFloor]) return;
    
    var floors = [...floorPlan.floors];
    var newElement = {
      element_id: "el_" + Date.now(),
      type: type,
      x: x || 100,
      y: y || 100,
      width: type === "label" ? 100 : 80,
      height: type === "label" ? 30 : 80,
      rotation: 0,
      label: type === "label" ? "Label" : "",
      unit_id: null,
      style: { fill: "#e5e7eb", stroke: "#9ca3af" }
    };
    floors[selectedFloor].elements.push(newElement);
    setFloorPlan({ ...floorPlan, floors: floors });
    setSelectedElement(newElement.element_id);
  }

  function updateElement(elementId, updates) {
    if (!floorPlan?.floors?.[selectedFloor]) return;
    
    var floors = [...floorPlan.floors];
    var elements = floors[selectedFloor].elements.map(function(el) {
      if (el.element_id === elementId) {
        return { ...el, ...updates };
      }
      return el;
    });
    floors[selectedFloor].elements = elements;
    setFloorPlan({ ...floorPlan, floors: floors });
  }

  function deleteElement(elementId) {
    if (!floorPlan?.floors?.[selectedFloor]) return;
    
    var floors = [...floorPlan.floors];
    floors[selectedFloor].elements = floors[selectedFloor].elements.filter(function(el) {
      return el.element_id !== elementId;
    });
    setFloorPlan({ ...floorPlan, floors: floors });
    setSelectedElement(null);
  }

  function getUnitStatus(unitId) {
    var unit = units.find(function(u) { return u.unit_id === unitId; });
    return unit?.status || "available";
  }

  function getUnitColor(unitId) {
    var status = getUnitStatus(unitId);
    var colors = floorPlan?.settings?.unit_colors || defaultColors;
    return colors[status] || colors.available;
  }

  function handleCanvasClick(e) {
    if (viewMode !== "edit" || selectedTool === "select") return;
    
    var rect = canvasRef.current.getBoundingClientRect();
    var x = (e.clientX - rect.left) / zoom;
    var y = (e.clientY - rect.top) / zoom;
    
    addElement(selectedTool, x, y);
    setSelectedTool("select");
  }

  function renderGridView() {
    var gridUnits = units.map(function(unit, index) {
      var status = unit.status || "available";
      var color = (floorPlan?.settings?.unit_colors || defaultColors)[status];
      
      return (
        <div
          key={unit.unit_id}
          data-testid={"grid-unit-" + unit.unit_id}
          onClick={function() { setUnitDetailDialog(unit); }}
          className="cursor-pointer transition-all hover:scale-105 hover:shadow-lg"
          style={{
            backgroundColor: color + "20",
            borderColor: color,
            borderWidth: 2,
            borderStyle: "solid",
            borderRadius: 8,
            padding: 16,
            minHeight: 120,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between"
          }}
        >
          <div>
            <div className="font-semibold text-sm mb-1">{unit.name}</div>
            <div className="text-xs text-gray-500">{unit.unit_type}</div>
            {unit.floor !== undefined && (
              <div className="text-xs text-gray-400">{t("floorPlan.floor")} {unit.floor}</div>
            )}
          </div>
          <Badge 
            variant="outline" 
            style={{ backgroundColor: color, color: "white", borderColor: color }}
            className="mt-2 w-fit text-xs"
          >
            {t("units.statuses." + status)}
          </Badge>
        </div>
      );
    });

    return (
      <div className="p-4">
        {units.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("floorPlan.noUnits")}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={function() { navigate("/properties"); }}
            >
              {t("floorPlan.addUnitsFirst")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {gridUnits}
          </div>
        )}
      </div>
    );
  }

  function renderInteractiveCanvas() {
    var currentFloor = floorPlan?.floors?.[selectedFloor];
    if (!currentFloor) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          <Layers className="w-12 h-12 mb-4 opacity-50" />
          <p>{t("floorPlan.noFloors")}</p>
          {canEdit && viewMode === "edit" && (
            <Button variant="outline" className="mt-4" onClick={addFloor}>
              <Plus className="w-4 h-4 mr-2" />
              {t("floorPlan.addFloor")}
            </Button>
          )}
        </div>
      );
    }

    var elements = currentFloor.elements.map(function(el) {
      var isSelected = selectedElement === el.element_id;
      var isUnitMarker = el.type === "unit_marker";
      var fillColor = isUnitMarker && el.unit_id ? getUnitColor(el.unit_id) : el.style?.fill || "#e5e7eb";
      var unit = isUnitMarker && el.unit_id ? units.find(function(u) { return u.unit_id === el.unit_id; }) : null;

      return (
        <g 
          key={el.element_id}
          transform={"translate(" + el.x + "," + el.y + ") rotate(" + (el.rotation || 0) + ")"}
          onClick={function(e) {
            e.stopPropagation();
            if (viewMode === "edit") {
              setSelectedElement(el.element_id);
            } else if (isUnitMarker && unit) {
              setUnitDetailDialog(unit);
            }
          }}
          style={{ cursor: isUnitMarker ? "pointer" : viewMode === "edit" ? "move" : "default" }}
        >
          {el.type === "room" && (
            <rect
              x={0} y={0}
              width={el.width || 80}
              height={el.height || 80}
              fill={fillColor}
              stroke={isSelected ? "#3b82f6" : el.style?.stroke || "#9ca3af"}
              strokeWidth={isSelected ? 3 : 2}
              rx={4}
            />
          )}
          {el.type === "unit_marker" && (
            <>
              <rect
                x={0} y={0}
                width={el.width || 80}
                height={el.height || 80}
                fill={fillColor + "40"}
                stroke={fillColor}
                strokeWidth={isSelected ? 3 : 2}
                rx={8}
              />
              {unit && (
                <text
                  x={(el.width || 80) / 2}
                  y={(el.height || 80) / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                  fontWeight="bold"
                  fill={fillColor}
                >
                  {unit.name}
                </text>
              )}
            </>
          )}
          {el.type === "door" && (
            <rect
              x={0} y={0}
              width={el.width || 40}
              height={el.height || 10}
              fill="#78716c"
              stroke={isSelected ? "#3b82f6" : "#57534e"}
              strokeWidth={isSelected ? 2 : 1}
            />
          )}
          {el.type === "stairs" && (
            <>
              <rect x={0} y={0} width={el.width || 60} height={el.height || 80} fill="#f3f4f6" stroke="#9ca3af" />
              {[0, 1, 2, 3, 4].map(function(i) {
                return <line key={i} x1={0} y1={i * 16} x2={el.width || 60} y2={i * 16} stroke="#9ca3af" />;
              })}
            </>
          )}
          {el.type === "label" && (
            <text
              x={0} y={15}
              fontSize={14}
              fill={isSelected ? "#3b82f6" : "#374151"}
              fontWeight={isSelected ? "bold" : "normal"}
            >
              {el.label || "Label"}
            </text>
          )}
        </g>
      );
    });

    return (
      <div className="relative overflow-auto border rounded-lg bg-white dark:bg-gray-900" style={{ maxHeight: "calc(100vh - 300px)" }}>
        <svg
          ref={canvasRef}
          width={currentFloor.width * zoom}
          height={currentFloor.height * zoom}
          onClick={handleCanvasClick}
          style={{ minWidth: "100%" }}
        >
          {showGrid && (
            <defs>
              <pattern id="grid" width={50 * zoom} height={50 * zoom} patternUnits="userSpaceOnUse">
                <path d={"M " + (50 * zoom) + " 0 L 0 0 0 " + (50 * zoom)} fill="none" stroke="#e5e7eb" strokeWidth="0.5" />
              </pattern>
            </defs>
          )}
          {showGrid && <rect width="100%" height="100%" fill="url(#grid)" />}
          
          {currentFloor.background_image && (
            <image
              href={currentFloor.background_image}
              x={0} y={0}
              width={currentFloor.width * zoom}
              height={currentFloor.height * zoom}
              opacity={0.5}
            />
          )}
          
          <g transform={"scale(" + zoom + ")"}>
            {elements}
          </g>
        </svg>
      </div>
    );
  }

  function renderImageView() {
    var currentFloor = floorPlan?.floors?.[selectedFloor];
    
    if (!currentFloor?.background_image) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500 border-2 border-dashed rounded-lg">
          <Image className="w-12 h-12 mb-4 opacity-50" />
          <p className="mb-4">{t("floorPlan.noImage")}</p>
          {canEdit && (
            <div className="text-center space-y-4">
              {/* Upload Button */}
              {storageConfigured && (
                <div className="relative inline-block">
                  <input 
                    type="file" 
                    onChange={handleFloorPlanImageUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept="image/*"
                    disabled={uploadingImage}
                  />
                  <Button variant="default" disabled={uploadingImage}>
                    {uploadingImage ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("fileUpload.uploading")}</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" />{t("floorPlan.uploadImage")}</>
                    )}
                  </Button>
                </div>
              )}
              
              <p className="text-sm text-gray-400">{t("common.or")}</p>
              
              {/* URL Input */}
              <div>
                <p className="text-sm text-gray-400 mb-2">{t("floorPlan.uploadImageHint")}</p>
                <Input
                  type="text"
                  placeholder={t("floorPlan.imageUrlPlaceholder")}
                  className="w-64"
                  onChange={function(e) {
                    if (e.target.value && currentFloor) {
                      var floors = [...(floorPlan?.floors || [])];
                      floors[selectedFloor] = { ...currentFloor, background_image: e.target.value };
                      setFloorPlan({ ...floorPlan, floors: floors });
                    }
                  }}
                />
              </div>
              
              {!storageConfigured && (
                <p className="text-xs text-amber-500">{t("fileUpload.configureInSettings")}</p>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative">
        {/* Change Image Button */}
        {canEdit && viewMode === "edit" && (
          <div className="absolute top-2 right-2 z-10">
            <div className="relative inline-block">
              <input 
                type="file" 
                onChange={handleFloorPlanImageUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept="image/*"
                disabled={uploadingImage}
              />
              <Button variant="secondary" size="sm" disabled={uploadingImage}>
                {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {t("floorPlan.changeImage")}
              </Button>
            </div>
          </div>
        )}
        
        <img
          src={currentFloor.background_image}
          alt="Floor Plan"
          className="max-w-full rounded-lg"
          style={{ transform: "scale(" + zoom + ")", transformOrigin: "top left" }}
        />
        {currentFloor.elements.filter(function(el) { return el.type === "unit_marker"; }).map(function(el) {
          var unit = el.unit_id ? units.find(function(u) { return u.unit_id === el.unit_id; }) : null;
          if (!unit) return null;
          
          return (
            <div
              key={el.element_id}
              className="absolute cursor-pointer transition-transform hover:scale-110"
              style={{
                left: el.x * zoom,
                top: el.y * zoom,
                backgroundColor: getUnitColor(el.unit_id),
                padding: "4px 8px",
                borderRadius: 4,
                color: "white",
                fontSize: 12,
                fontWeight: "bold"
              }}
              onClick={function() { setUnitDetailDialog(unit); }}
            >
              {unit.name}
            </div>
          );
        })}
      </div>
    );
  }

  function renderSchematicView() {
    var floorGroups = {};
    units.forEach(function(unit) {
      var floor = unit.floor || 0;
      if (!floorGroups[floor]) floorGroups[floor] = [];
      floorGroups[floor].push(unit);
    });

    var sortedFloors = Object.keys(floorGroups).sort(function(a, b) { return Number(b) - Number(a); });

    return (
      <div className="space-y-6 p-4">
        {sortedFloors.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("floorPlan.noUnits")}</p>
          </div>
        ) : (
          sortedFloors.map(function(floor) {
            var floorUnits = floorGroups[floor];
            return (
              <Card key={floor}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    {Number(floor) === 0 ? t("floorPlan.groundFloor") : t("floorPlan.floor") + " " + floor}
                    <Badge variant="outline" className="ml-auto">{floorUnits.length} {t("floorPlan.units")}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {floorUnits.map(function(unit) {
                      var color = (floorPlan?.settings?.unit_colors || defaultColors)[unit.status || "available"];
                      return (
                        <div
                          key={unit.unit_id}
                          className="px-3 py-2 rounded cursor-pointer hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: color, color: "white" }}
                          onClick={function() { setUnitDetailDialog(unit); }}
                        >
                          <div className="font-medium text-sm">{unit.name}</div>
                          <div className="text-xs opacity-80">{unit.unit_type}</div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    );
  }

  function renderElementProperties() {
    if (!selectedElement || viewMode !== "edit") return null;
    
    var currentFloor = floorPlan?.floors?.[selectedFloor];
    var element = currentFloor?.elements?.find(function(el) { return el.element_id === selectedElement; });
    if (!element) return null;

    return (
      <Card className="absolute right-4 top-4 w-64 z-10">
        <CardHeader className="py-2 px-3">
          <CardTitle className="text-sm">{t("floorPlan.elementProperties")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 py-2 px-3">
          <div>
            <Label className="text-xs">{t("floorPlan.position")}</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={element.x}
                onChange={function(e) { updateElement(selectedElement, { x: Number(e.target.value) }); }}
                className="h-8"
              />
              <Input
                type="number"
                value={element.y}
                onChange={function(e) { updateElement(selectedElement, { y: Number(e.target.value) }); }}
                className="h-8"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("floorPlan.size")}</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={element.width || 80}
                onChange={function(e) { updateElement(selectedElement, { width: Number(e.target.value) }); }}
                className="h-8"
              />
              <Input
                type="number"
                value={element.height || 80}
                onChange={function(e) { updateElement(selectedElement, { height: Number(e.target.value) }); }}
                className="h-8"
              />
            </div>
          </div>
          {element.type === "label" && (
            <div>
              <Label className="text-xs">{t("floorPlan.labelText")}</Label>
              <Input
                value={element.label || ""}
                onChange={function(e) { updateElement(selectedElement, { label: e.target.value }); }}
                className="h-8"
              />
            </div>
          )}
          {element.type === "unit_marker" && (
            <div>
              <Label className="text-xs">{t("floorPlan.linkedUnit")}</Label>
              <Select
                value={element.unit_id || "none"}
                onValueChange={function(val) { updateElement(selectedElement, { unit_id: val === "none" ? null : val }); }}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("common.none")}</SelectItem>
                  {units.map(function(u) {
                    return <SelectItem key={u.unit_id} value={u.unit_id}>{u.name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="destructive"
            size="sm"
            className="w-full"
            onClick={function() { deleteElement(selectedElement); }}
          >
            <Trash2 className="w-3 h-3 mr-1" />
            {t("common.delete")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t("floorPlan.propertyNotFound")}</p>
        <Button variant="outline" className="mt-4" onClick={function() { navigate("/properties"); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("common.back")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="floor-plan-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={function() { navigate("/properties"); }}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t("floorPlan.title")} - {property.name}
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {property.address}, {property.city}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Button
                variant={viewMode === "edit" ? "default" : "outline"}
                size="sm"
                onClick={function() { setViewMode(viewMode === "edit" ? "view" : "edit"); }}
              >
                {viewMode === "edit" ? <Eye className="w-4 h-4 mr-1" /> : <Edit3 className="w-4 h-4 mr-1" />}
                {viewMode === "edit" ? t("floorPlan.viewMode") : t("floorPlan.editMode")}
              </Button>
              <Button size="sm" onClick={saveFloorPlan} disabled={saving}>
                <Save className="w-4 h-4 mr-1" />
                {saving ? t("common.saving") : t("common.save")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* View Type Tabs */}
      <Tabs value={viewType} onValueChange={setViewType}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="grid" data-testid="view-tab-grid">
            <Grid3X3 className="w-4 h-4 mr-1" />
            {t("floorPlan.gridView")}
          </TabsTrigger>
          <TabsTrigger value="schematic" data-testid="view-tab-schematic">
            <Layers className="w-4 h-4 mr-1" />
            {t("floorPlan.schematicView")}
          </TabsTrigger>
          <TabsTrigger value="image" data-testid="view-tab-image">
            <Image className="w-4 h-4 mr-1" />
            {t("floorPlan.imageView")}
          </TabsTrigger>
          <TabsTrigger value="interactive" data-testid="view-tab-interactive">
            <Edit3 className="w-4 h-4 mr-1" />
            {t("floorPlan.interactiveView")}
          </TabsTrigger>
        </TabsList>

        {/* Editor Toolbar for Interactive & Image Views */}
        {viewMode === "edit" && (viewType === "interactive" || viewType === "image") && (
          <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg mt-2">
            {/* Floor Management */}
            <div className="flex items-center gap-2 mr-4">
              <Select value={String(selectedFloor)} onValueChange={function(v) { setSelectedFloor(Number(v)); }}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(floorPlan?.floors || []).map(function(f, i) {
                    return <SelectItem key={i} value={String(i)}>{f.floor_name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={addFloor}>
                <Plus className="w-3 h-3" />
              </Button>
              {(floorPlan?.floors?.length || 0) > 1 && (
                <Button size="sm" variant="outline" onClick={function() { deleteFloor(selectedFloor); }}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Element Tools */}
            {viewType === "interactive" && (
              <div className="flex items-center gap-1 border-l pl-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={selectedTool === "select" ? "default" : "outline"}
                        onClick={function() { setSelectedTool("select"); }}
                      >
                        <Move className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("floorPlan.selectTool")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                {elementTypes.map(function(et) {
                  var Icon = et.icon;
                  return (
                    <TooltipProvider key={et.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant={selectedTool === et.id ? "default" : "outline"}
                            onClick={function() { setSelectedTool(et.id); }}
                          >
                            <Icon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("floorPlan." + et.id)}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            )}

            {/* View Controls */}
            <div className="flex items-center gap-1 border-l pl-4 ml-auto">
              <Button size="sm" variant="outline" onClick={function() { setZoom(Math.max(0.5, zoom - 0.25)); }}>
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
              <Button size="sm" variant="outline" onClick={function() { setZoom(Math.min(2, zoom + 0.25)); }}>
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={function() { setZoom(1); }}>
                <RotateCcw className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1 ml-2">
                <Switch checked={showGrid} onCheckedChange={setShowGrid} />
                <span className="text-xs">{t("floorPlan.showGrid")}</span>
              </div>
            </div>
          </div>
        )}

        <TabsContent value="grid" className="mt-4">
          {renderGridView()}
        </TabsContent>
        
        <TabsContent value="schematic" className="mt-4">
          {renderSchematicView()}
        </TabsContent>
        
        <TabsContent value="image" className="mt-4 relative">
          {renderImageView()}
          {renderElementProperties()}
        </TabsContent>
        
        <TabsContent value="interactive" className="mt-4 relative">
          {renderInteractiveCanvas()}
          {renderElementProperties()}
        </TabsContent>
      </Tabs>

      {/* Status Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium">{t("floorPlan.legend")}:</span>
            {Object.entries(floorPlan?.settings?.unit_colors || defaultColors).map(function([status, color]) {
              return (
                <div key={status} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                  <span className="text-sm">{t("units.statuses." + status)}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Unit Detail Dialog */}
      <Dialog open={!!unitDetailDialog} onOpenChange={function() { setUnitDetailDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{unitDetailDialog?.name}</DialogTitle>
          </DialogHeader>
          {unitDetailDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">{t("units.unitType")}</Label>
                  <p className="font-medium">{t("units.types." + unitDetailDialog.unit_type)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">{t("common.status")}</Label>
                  <Badge 
                    style={{ 
                      backgroundColor: (floorPlan?.settings?.unit_colors || defaultColors)[unitDetailDialog.status || "available"],
                      color: "white"
                    }}
                  >
                    {t("units.statuses." + (unitDetailDialog.status || "available"))}
                  </Badge>
                </div>
                {unitDetailDialog.floor !== undefined && (
                  <div>
                    <Label className="text-gray-500">{t("units.floor")}</Label>
                    <p className="font-medium">{unitDetailDialog.floor}</p>
                  </div>
                )}
                {unitDetailDialog.area_sqm && (
                  <div>
                    <Label className="text-gray-500">{t("units.size")}</Label>
                    <p className="font-medium">{unitDetailDialog.area_sqm} m²</p>
                  </div>
                )}
                {unitDetailDialog.rent_price && (
                  <div>
                    <Label className="text-gray-500">{t("units.rentPrice")}</Label>
                    <p className="font-medium">{unitDetailDialog.rent_price}</p>
                  </div>
                )}
                {unitDetailDialog.rooms && (
                  <div>
                    <Label className="text-gray-500">{t("units.numRooms")}</Label>
                    <p className="font-medium">{unitDetailDialog.rooms}</p>
                  </div>
                )}
              </div>
              {unitDetailDialog.description && (
                <div>
                  <Label className="text-gray-500">{t("common.description")}</Label>
                  <p className="text-sm">{unitDetailDialog.description}</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={function() { 
                    setUnitDetailDialog(null);
                    navigate("/properties?unit=" + unitDetailDialog.unit_id);
                  }}
                >
                  {t("floorPlan.viewUnitDetails")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
